import { useEffect, useMemo, useState } from "react";
import {
  createCleaner,
  deleteCleaner,
  fetchCleaners,
} from "../../../store/slices/cleanerSlice";
import { loadCurrentUser } from "../../../store/slices/authSlice";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { useTranslation } from "../../../translations/i18n";
import { CreateCleanerRequest } from "../../../models/RequestModels/CreateCleanerRequest";
import { AuthUserResponse } from "../../../models/ResponseModels/AuthUserResponse";
import {
  getApiErrorMessage,
  isSubscriptionLimitPayload,
} from "../../../utils/helperFunctions/apiErrorHelper";

export const useCleanersScreen = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { cleaners, loading: cleanersLoading } = useAppSelector(
    (state) => state.cleaners,
  );

  const [cleanerModalOpen, setCleanerModalOpen] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");

  useEffect(() => {
    dispatch(fetchCleaners());
  }, [dispatch]);

  const refresh = () => {
    dispatch(fetchCleaners());
  };

  const openCleanerModal = () => {
    setCleanerModalOpen(true);
  };

  const closeCleanerModal = () => {
    setCleanerModalOpen(false);
  };

  const closeUpgradeModal = () => {
    setUpgradeModalOpen(false);
    setUpgradeMessage("");
  };

  const submitCleaner = async (body: CreateCleanerRequest) => {
    const result = await dispatch(createCleaner(body));

    if (createCleaner.fulfilled.match(result)) {
      closeCleanerModal();
      dispatch(loadCurrentUser());
      return;
    }

    if (isSubscriptionLimitPayload(result.payload)) {
      closeCleanerModal();
      setUpgradeMessage(
        getApiErrorMessage(
          result.payload,
          t("subscription.upgradeRequiredMessage"),
        ),
      );
      setUpgradeModalOpen(true);
      return;
    }

    setUpgradeMessage(
      getApiErrorMessage(result.payload, t("cleaners.failedCreateCleaner")),
    );
    setUpgradeModalOpen(true);
  };

  const removeCleaner = async (cleaner: AuthUserResponse) => {
    if (!window.confirm(t("cleaners.deleteQuestion", { name: cleaner.name }))) {
      return;
    }

    await dispatch(deleteCleaner(cleaner.id));
    dispatch(loadCurrentUser());
  };

  const managers = useMemo(() => {
    return cleaners.filter((cleaner) => cleaner.role === "manager");
  }, [cleaners]);

  const housekeepers = useMemo(() => {
    return cleaners.filter((cleaner) => cleaner.role === "cleaner");
  }, [cleaners]);

  const viewers = useMemo(() => {
    return cleaners.filter((cleaner) => cleaner.role === "viewer");
  }, [cleaners]);

  return {
    t,
    cleaners,
    managers,
    housekeepers,
    viewers,
    loading: cleanersLoading,
    refresh,

    cleanerModalOpen,
    openCleanerModal,
    closeCleanerModal,
    submitCleaner,

    removeCleaner,

    upgradeModalOpen,
    upgradeMessage,
    closeUpgradeModal,
  };
};
